import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { CTOneIDProvider } from '@ct/oneid/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { HelmetProvider } from 'react-helmet-async';
import { TooltipProvider } from '@vta/ui';
import { env } from './env.ts';
import { createQueryClient } from './queryClient.ts';
import { createAppRouter } from './router.tsx';
import './styles/globals.css';

/**
 * Bootstrap order:
 *   StrictMode → HelmetProvider (head tags) → CTOneIDProvider (One ID identity
 *   context; `useCTUser()` reads it) → QueryClientProvider (must wrap the
 *   router so route loaders can call `queryClient.ensureQueryData`) →
 *   TooltipProvider (radix singleton) → RouterProvider.
 *
 * The QueryClient is created once at module scope so HMR does not reset the
 * cache and re-trigger every globe-data fetch.
 */
const queryClient = createQueryClient();
const router = createAppRouter(queryClient);

const root = document.getElementById('root');
if (!root) throw new Error('#root missing');

createRoot(root).render(
  <StrictMode>
    <HelmetProvider>
      <CTOneIDProvider baseURL={env.VITE_ONEID_BASE_URL}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider delayDuration={120} skipDelayDuration={300}>
            <RouterProvider router={router} />
          </TooltipProvider>
        </QueryClientProvider>
      </CTOneIDProvider>
    </HelmetProvider>
  </StrictMode>,
);
